import type { JSX } from 'react'
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useRouterState
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { BOT_STATES } from '@apiary/shared'
import type { BotState } from '@apiary/shared'
import { AppFrame } from '@/ui/layout/AppFrame'
import { useKeyboardMode } from '@/ui/hooks/useKeyboardMode'
import { ShortcutsDialog } from '@/components/ShortcutsDialog'
import { Toaster } from '@/components/ui/Toaster'
import { useSessionStore } from '@/stores/session'
import { SignInScreen } from '@/screens/auth/SignInScreen'
import { SignUpScreen } from '@/screens/auth/SignUpScreen'
import { DashboardScreen } from '@/screens/dashboard/DashboardScreen'
import { BotsScreen } from '@/screens/bots/BotsScreen'
import { BotScreen } from '@/screens/bots/BotScreen'
import { CreateBotScreen } from '@/screens/bots/CreateBotScreen'
import { AccountsScreen } from '@/screens/accounts/AccountsScreen'
import { ServersScreen } from '@/screens/servers/ServersScreen'
import { SettingsScreen } from '@/screens/settings/SettingsScreen'
import { LogsScreen } from '@/screens/logs/LogsScreen'
import { AuditScreen } from '@/screens/audit/AuditScreen'
import { AlertsScreen } from '@/screens/alerts/AlertsScreen'

/**
 * Роутер (TanStack Router, code-based). Дерево описано вручную, без
 * file-based генерации: маршрутов немного, а сгенерированный `routeTree.gen.ts`
 * пришлось бы коммитить и мержить втроём.
 *
 * Гард: всё под `appRoute` требует аутентификации. Статус берётся из zustand,
 * который заполняет bootstrap в `App` (силовой refresh + `/auth/me`).
 */

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout
})

function RootLayout(): JSX.Element {
  return (
    <>
      <Outlet />
      <Toaster />
      <ShortcutsDialog />
    </>
  )
}

/** Заголовок в app-bar по текущему пути: одна таблица, без дублей в экранах. */
const TITLE_BY_PREFIX: [string, string][] = [
  ['/bots/new', 'wizard.title'],
  ['/bots', 'bots.title'],
  ['/accounts', 'accounts.title'],
  ['/servers', 'servers.title'],
  ['/logs', 'nav.logs'],
  ['/audit', 'nav.audit'],
  ['/alerts', 'nav.alerts'],
  ['/settings', 'settings.title']
]

function AppLayout(): JSX.Element {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: state => state.location.pathname })
  const match = TITLE_BY_PREFIX.find(([prefix]) => pathname.startsWith(prefix))
  const title = t(match?.[1] ?? 'dashboard.title')

  // Клавиатурный режим включается здесь, один раз на всё приложение: от него
  // зависит видимость курсора списков (см. useKeyboardMode).
  useKeyboardMode()

  return (
    <AppFrame title={title}>
      <Outlet />
    </AppFrame>
  )
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: SignInScreen
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: SignUpScreen
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: () => {
    if (useSessionStore.getState().status !== 'authenticated') {
      throw redirect({ to: '/login' })
    }
  },
  component: AppLayout
})

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardScreen
})

const botsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bots',
  // `?state=PLAYING` — фильтр в адресе, а не только в памяти компонента: по
  // клику в легенде дашборда открывается отфильтрованный список, и ссылку
  // можно сохранить или отправить. Неизвестное значение просто игнорируется —
  // чужой параметр в URL не должен ломать страницу.
  validateSearch: (search: Record<string, unknown>): { state?: BotState } => {
    const raw = search['state']
    return typeof raw === 'string' && (BOT_STATES as readonly string[]).includes(raw)
      ? { state: raw as BotState }
      : {}
  },
  component: BotsRoute
})

function BotsRoute(): JSX.Element {
  const { state } = botsRoute.useSearch()
  return <BotsScreen initialState={state ?? ''} />
}

const createBotRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bots/new',
  component: CreateBotScreen
})

const botDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bots/$botId',
  // `?camera=1` — открыть камеру сразу. Так работает кнопка камеры в списке:
  // тридцать WebGL-контекстов на списке невозможны (док 08 §1), поэтому она
  // ведёт на страницу бота, а не рисует поток в плитке.
  validateSearch: (search: Record<string, unknown>): { camera?: boolean } => {
    const raw = search['camera']
    return raw === '1' || raw === true ? { camera: true } : {}
  },
  component: BotDetailRoute
})

function BotDetailRoute(): JSX.Element {
  const { botId } = botDetailRoute.useParams()
  const { camera } = botDetailRoute.useSearch()
  return <BotScreen botId={botId} autoCamera={camera === true} />
}

const accountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounts',
  component: AccountsScreen
})

const serversRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/servers',
  component: ServersScreen
})

const logsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/logs',
  component: LogsScreen
})

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  component: AuditScreen
})

/**
 * Алерты (docs/06 §7.1). Роль здесь НЕ проверяется: `beforeLoad` умеет только
 * редиректить, а «правил алертов вам не видно» — это объяснение, а не 404.
 * Экран сам решает, что показать (см. `AlertsScreen`), а пункт меню скрыт.
 */
const alertsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/alerts',
  component: AlertsScreen
})

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsScreen
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  appRoute.addChildren([
    dashboardRoute,
    botsRoute,
    createBotRoute,
    botDetailRoute,
    accountsRoute,
    serversRoute,
    logsRoute,
    auditRoute,
    alertsRoute,
    settingsRoute
  ])
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent'
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

// Регистрация даёт типизированные `Link to=` и `useParams` по всему приложению.
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}

export { routeTree }
